import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import Header from '../components/Header';

const CancellationPolicy = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="pt-32 pb-20">
        <div className="container mx-auto px-4 max-w-4xl">
          <Link to="/">
            <Button variant="outline" className="mb-6">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </Link>

          <div className="bg-white rounded-xl shadow-md p-8 md:p-12">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Cancellation Policy
            </h1>
            <p className="text-gray-600 mb-8">
              <strong>Effective Date:</strong> December 2025<br />
              <strong>Last Updated:</strong> December 2025
            </p>

            <div className="space-y-6 text-gray-700">
              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-3">1. Overview</h2>
                <p>
                  At Livington Designs, we understand that circumstances change and projects sometimes need to be postponed or cancelled. This Cancellation Policy outlines the terms and conditions for cancelling or rescheduling painting services.
                </p>
                <p className="mt-3">
                  We strive to be fair and transparent in our cancellation policies while protecting both our customers and our business operations.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-3">2. Estimate Cancellation</h2>
                
                <h3 className="text-xl font-semibold text-gray-900 mb-2 mt-4">2.1 Free Estimate Requests</h3>
                <p>
                  If you have requested a free estimate but have not yet signed a contract, you may cancel at any time without penalty. Simply contact us to cancel your scheduled consultation.
                </p>

                <h3 className="text-xl font-semibold text-gray-900 mb-2 mt-4">2.2 Notice for Estimate Cancellation</h3>
                <p>
                  We appreciate at least 24 hours' notice if you need to cancel or reschedule an estimate appointment. This allows us to offer the time slot to other customers and maintain efficient scheduling.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-3">3. Contract Cancellation Before Work Begins</h2>
                
                <h3 className="text-xl font-semibold text-gray-900 mb-2 mt-4">3.1 Cancellation Within 72 Hours of Signing</h3>
                <p>
                  If you cancel within 72 hours (3 business days) of signing the contract and before any work has commenced:
                </p>
                <ul className="list-disc ml-6 mt-2 space-y-1">
                  <li>You will receive a full refund of your deposit</li>
                  <li>No cancellation fees will be charged</li>
                  <li>Cancellation must be submitted in writing (email or written notice)</li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-900 mb-2 mt-4">3.2 Cancellation After 72 Hours But Before Work Begins</h3>
                <p>
                  If you cancel more than 72 hours after signing but before work begins:
                </p>
                <ul className="list-disc ml-6 mt-2 space-y-2">
                  <li><strong>More than 7 days before scheduled start:</strong> 15% cancellation fee; 85% of deposit refunded</li>
                  <li><strong>3-7 days before scheduled start:</strong> 25% cancellation fee; 75% of deposit refunded</li>
                  <li><strong>Less than 3 days before scheduled start:</strong> 50% cancellation fee; 50% of deposit refunded</li>
                </ul>
                <p className="mt-3">
                  Cancellation fees cover administrative costs, scheduling commitments, materials ordered, and lost opportunity costs.
                </p>

                <h3 className="text-xl font-semibold text-gray-900 mb-2 mt-4">3.3 Materials Already Ordered</h3>
                <p>
                  If custom-colored paint or specialized materials have been ordered specifically for your project, you will be responsible for:
                </p>
                <ul className="list-disc ml-6 mt-2 space-y-1">
                  <li>Full cost of non-returnable custom materials</li>
                  <li>Any restocking fees for returnable materials</li>
                </ul>
                <p className="mt-2">
                  These costs will be deducted from your deposit refund.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-3">4. Cancellation After Work Has Begun</h2>
                
                <h3 className="text-xl font-semibold text-gray-900 mb-2 mt-4">4.1 Partial Project Cancellation</h3>
                <p>
                  If you wish to cancel after work has commenced:
                </p>
                <ul className="list-disc ml-6 mt-2 space-y-2">
                  <li>You will be charged for all work completed to date</li>
                  <li>You will be charged for all materials used or ordered</li>
                  <li>You will be charged for cleanup and site restoration</li>
                  <li>Any remaining deposit after these charges will be refunded</li>
                  <li>If costs exceed the deposit, payment is due immediately</li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-900 mb-2 mt-4">4.2 Work Stoppage</h3>
                <p>
                  If work is stopped mid-project due to customer cancellation:
                </p>
                <ul className="list-disc ml-6 mt-2 space-y-1">
                  <li>We will complete necessary steps to leave surfaces in a safe, stable condition</li>
                  <li>Partially completed areas may require additional finishing by another contractor</li>
                  <li>We are not responsible for touch-ups or warranty on incomplete work</li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-900 mb-2 mt-4">4.3 Final Invoice</h3>
                <p>
                  A detailed final invoice will be provided within 5 business days of cancellation, itemizing:
                </p>
                <ul className="list-disc ml-6 mt-2 space-y-1">
                  <li>Labor hours and work completed</li>
                  <li>Materials used</li>
                  <li>Equipment and supplies</li>
                  <li>Applicable cancellation fees</li>
                  <li>Refund amount (if any)</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-3">5. Rescheduling Projects</h2>
                
                <h3 className="text-xl font-semibold text-gray-900 mb-2 mt-4">5.1 Rescheduling Before Work Begins</h3>
                <p>
                  We understand that plans change. You may reschedule your project:
                </p>
                <ul className="list-disc ml-6 mt-2 space-y-2">
                  <li><strong>More than 7 days before start:</strong> No rescheduling fee; deposit remains valid</li>
                  <li><strong>3-7 days before start:</strong> $100 rescheduling fee</li>
                  <li><strong>Less than 3 days before start:</strong> $250 rescheduling fee</li>
                </ul>
                <p className="mt-3">
                  Rescheduled projects will be assigned the next available time slot, which may not be immediate.
                </p>

                <h3 className="text-xl font-semibold text-gray-900 mb-2 mt-4">5.2 Multiple Rescheduling</h3>
                <p>
                  If a project is rescheduled more than twice, we reserve the right to:
                </p>
                <ul className="list-disc ml-6 mt-2 space-y-1">
                  <li>Charge additional rescheduling fees</li>
                  <li>Re-evaluate pricing based on current rates</li>
                  <li>Require full payment before rescheduling</li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-900 mb-2 mt-4">5.3 Deposit Validity</h3>
                <p>
                  Deposits for rescheduled projects remain valid for 6 months from the original contract date. After 6 months, the contract may be cancelled with applicable fees.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-3">6. Cancellation by Livington Designs</h2>
                
                <h3 className="text-xl font-semibold text-gray-900 mb-2 mt-4">6.1 Our Right to Cancel</h3>
                <p>
                  Livington Designs reserves the right to cancel or postpone services if:
                </p>
                <ul className="list-disc ml-6 mt-2 space-y-2">
                  <li>Weather conditions make work unsafe or impossible (exterior projects)</li>
                  <li>Site conditions are unsafe or unsuitable for work</li>
                  <li>Customer fails to fulfill their contractual obligations</li>
                  <li>Payment terms are not met</li>
                  <li>Hazardous materials are discovered that were not disclosed</li>
                  <li>Access to the work site is denied or restricted</li>
                  <li>Emergency situations or unforeseen circumstances</li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-900 mb-2 mt-4">6.2 Full Refund Scenarios</h3>
                <p>
                  If we cancel your project before work begins due to our inability to perform, you will receive:
                </p>
                <ul className="list-disc ml-6 mt-2 space-y-1">
                  <li>Full refund of all payments made</li>
                  <li>No cancellation fees</li>
                  <li>Refund processed within 10 business days</li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-900 mb-2 mt-4">6.3 Weather-Related Delays</h3>
                <p>
                  For exterior painting projects affected by weather:
                </p>
                <ul className="list-disc ml-6 mt-2 space-y-1">
                  <li>Projects will be rescheduled at no additional charge</li>
                  <li>Deposits remain valid</li>
                  <li>We will communicate promptly about delays</li>
                  <li>No refund or cancellation fees apply for weather delays</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-3">7. How to Cancel or Reschedule</h2>
                
                <h3 className="text-xl font-semibold text-gray-900 mb-2 mt-4">7.1 Written Notice Required</h3>
                <p>
                  All cancellations and reschedule requests must be submitted in writing via:
                </p>
                <ul className="list-disc ml-6 mt-2 space-y-1">
                  <li>Email to: <a href="mailto:info@livingtone.store" className="text-blue-900 hover:underline">info@livingtone.store</a></li>
                  <li>Written letter delivered to our office address</li>
                  <li>Certified mail (for formal documentation)</li>
                </ul>
                <p className="mt-3">
                  Phone calls alone do not constitute official cancellation.
                </p>

                <h3 className="text-xl font-semibold text-gray-900 mb-2 mt-4">7.2 Required Information</h3>
                <p>Your cancellation/reschedule request should include:</p>
                <ul className="list-disc ml-6 mt-2 space-y-1">
                  <li>Your name and contact information</li>
                  <li>Contract number or date</li>
                  <li>Reason for cancellation/rescheduling (optional but appreciated)</li>
                  <li>Preferred new dates (for rescheduling)</li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-900 mb-2 mt-4">7.3 Confirmation</h3>
                <p>
                  We will confirm receipt of your cancellation/reschedule request within 1 business day and provide:
                </p>
                <ul className="list-disc ml-6 mt-2 space-y-1">
                  <li>Written confirmation</li>
                  <li>Applicable fees (if any)</li>
                  <li>Refund amount and timeline</li>
                  <li>New project dates (for rescheduling)</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-3">8. Refund Processing</h2>
                
                <h3 className="text-xl font-semibold text-gray-900 mb-2 mt-4">8.1 Refund Timeline</h3>
                <p>Approved refunds will be processed as follows:</p>
                <ul className="list-disc ml-6 mt-2 space-y-1">
                  <li><strong>Check payments:</strong> Refund check mailed within 10 business days</li>
                  <li><strong>Credit/Debit card payments:</strong> Refund processed within 5-7 business days (may take additional time to appear on statement)</li>
                  <li><strong>Bank transfers:</strong> Refund initiated within 5 business days</li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-900 mb-2 mt-4">8.2 Refund Method</h3>
                <p>
                  Refunds will be issued using the same payment method as the original payment. If this is not possible, we will coordinate an alternative method with you.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-3">9. Emergency Situations</h2>
                <p>
                  We understand that genuine emergencies occur. In cases of:
                </p>
                <ul className="list-disc ml-6 mt-2 space-y-1">
                  <li>Serious illness or hospitalization</li>
                  <li>Death in the family</li>
                  <li>Natural disasters</li>
                  <li>Other documented emergencies</li>
                </ul>
                <p className="mt-3">
                  We will work with you on a case-by-case basis to find a fair solution, which may include waiving or reducing cancellation fees. Documentation may be required.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-3">10. Disputes and Questions</h2>
                <p>
                  If you have questions about this Cancellation Policy or wish to dispute a cancellation fee, please contact us:
                </p>
                <div className="bg-gray-50 p-6 rounded-lg mt-4">
                  <p><strong>Livington Designs</strong></p>
                  <p className="mt-2">4512 Sunset Boulevard<br />Los Angeles, CA 90027</p>
                  <p className="mt-2">Phone: <a href="tel:+18717871113" className="text-blue-900 hover:underline">+1-871-7871-113</a></p>
                  <p>Email: <a href="mailto:info@livingtone.store" className="text-blue-900 hover:underline">info@livingtone.store</a></p>
                  <p className="mt-3 text-sm">Business Hours: Mon-Fri 8:00 AM - 6:00 PM, Sat 9:00 AM - 4:00 PM</p>
                </div>
                <p className="mt-4">
                  We are committed to resolving any concerns fairly and professionally. Most disputes can be resolved through direct communication.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-3">11. Policy Changes</h2>
                <p>
                  This Cancellation Policy may be updated from time to time. Changes will be posted on our website with an updated "Last Updated" date. The policy in effect at the time of your contract signing will apply to your project.
                </p>
              </section>

              <section className="border-t border-gray-200 pt-6 mt-8">
                <p className="text-sm text-gray-600">
                  By signing a contract with Livington Designs, you acknowledge that you have read, understood, and agree to this Cancellation Policy.
                </p>
              </section>
            </div>
          </div>
        </div>
      </div>

      {/* Simple Footer */}
      <footer className="bg-gray-900 text-white py-8">
        <div className="container mx-auto px-4 text-center">
          <p className="text-gray-400 text-sm">
            © {new Date().getFullYear()} Livington Designs. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default CancellationPolicy;
